import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/base/BackToTop';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, getArticlesByCategory, getCategoryBySlug } from '@/mocks/articles';

const articleImages: Record<string, string> = {
  'official-login-guide-2024': 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20login%20page%20on%20a%20laptop%20screen%20with%20warm%20golden%20accents%2C%20clean%20professional%20financial%20platform%20interface%2C%20soft%20natural%20lighting%2C%20elegant%20workspace%20setting%2C%20warm%20beige%20background%2C%20editorial%20technology%20photography&width=640&height=400&seq=article-login-guide&orientation=landscape',
  'login-problem-solutions': 'https://readdy.ai/api/search-image?query=Customer%20support%20and%20help%20desk%20scene%20with%20warm%20ambient%20lighting%2C%20professional%20office%20environment%2C%20clean%20minimalist%20design%2C%20helpful%20atmosphere%2C%20warm%20beige%20tones&width=640&height=400&seq=article-login-problems&orientation=landscape',
  'platform-features-overview': 'https://readdy.ai/api/search-image?query=Modern%20financial%20trading%20platform%20dashboard%20on%20large%20monitor%20screen%2C%20clean%20minimalist%20interface%20showing%20market%20charts%2C%20professional%20trading%20environment%2C%20warm%20golden%20and%20charcoal%20color%20scheme&width=640&height=400&seq=article-platform-features&orientation=landscape',
  'market-trends-2025': 'https://readdy.ai/api/search-image?query=Professional%20financial%20market%20analysis%20scene%20with%20multiple%20screens%20showing%20stock%20charts%20and%20data%2C%20modern%20minimalist%20office%2C%20warm%20ambient%20lighting%2C%20clean%20professional%20aesthetic&width=640&height=400&seq=article-market-trends&orientation=landscape',
  'account-security-guide': 'https://readdy.ai/api/search-image?query=Digital%20security%20and%20cybersecurity%20concept%20with%20shield%20icon%20on%20screen%2C%20warm%20lighting%2C%20modern%20tech%20office%2C%20professional%20atmosphere%2C%20clean%20minimalist%20design&width=640&height=400&seq=article-security-guide&orientation=landscape',
  'fund-safety-system': 'https://readdy.ai/api/search-image?query=Financial%20security%20and%20safety%20concept%2C%20bank%20vault%20aesthetic%20in%20modern%20minimalist%20style%2C%20warm%20golden%20lighting%2C%20trust%20and%20protection%20atmosphere&width=640&height=400&seq=article-fund-safety&orientation=landscape',
  'deposit-withdrawal-guide': 'https://readdy.ai/api/search-image?query=Digital%20payment%20and%20banking%20transaction%20concept%2C%20modern%20financial%20interface%20on%20screen%2C%20warm%20lighting%2C%20clean%20professional%20setting%2C%20minimalist%20design&width=640&height=400&seq=article-deposit-guide&orientation=landscape',
  'new-user-registration-guide': 'https://readdy.ai/api/search-image?query=New%20user%20registration%20and%20onboarding%20concept%2C%20modern%20smartphone%20displaying%20signup%20form%2C%20warm%20lighting%2C%20clean%20minimalist%20environment&width=640&height=400&seq=article-registration-guide&orientation=landscape',
};

const defaultImage = 'https://readdy.ai/api/search-image?query=Professional%20financial%20content%20article%20image%20with%20warm%20golden%20accents%2C%20clean%20minimalist%20design%2C%20modern%20corporate%20aesthetic&width=640&height=400&seq=article-default&orientation=landscape';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = getCategoryBySlug(slug || '');
  const articles = getArticlesByCategory(slug || '');

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-3">分类未找到</h2>
            <Link to="/" className="text-primary-600 hover:text-primary-700 cursor-pointer">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: category.seoTitle,
    description: category.seoDescription,
    url: `https://ovelbrf.cn/category/${slug}`,
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={category.seoTitle}
        description={category.seoDescription}
        keywords={category.seoKeywords}
        canonicalUrl={`https://ovelbrf.cn/category/${slug}`}
      />
      <script type="application/ld+json">{JSON.stringify(collectionSchema)}</script>

      <Navbar />
      <main className="pt-20 pb-16">
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category.name },
              ]}
            />
          </div>

          <div className="mb-10">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              {category.name}
            </h1>
            <p className="text-sm md:text-base text-foreground-500 max-w-2xl">{category.description}</p>
          </div>

          <div className="flex flex-wrap gap-2 mb-10">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {articles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group rounded-xl overflow-hidden bg-background-100 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={articleImages[article.slug] || defaultImage}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-xs text-foreground-400">{article.date}</span>
                      <span className="text-xs text-foreground-400">{article.readTime}</span>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-500 line-clamp-2 leading-relaxed">{article.summary}</p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 mx-auto mb-4">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类下暂无文章</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}